import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import {
  FaExclamationTriangle, FaTimes, FaDownload, FaFilePdf, FaImages,
  FaReceipt, FaSpinner, FaCheckCircle, FaMapMarkerAlt, FaCalendarAlt
} from 'react-icons/fa';
import { getUserItineraries, getItinerary, getPhotos, getExpenses, cancelSubscription } from '../api/client';
import html2pdf from 'html2pdf.js';

const CancellationModal = ({ isOpen, onClose, onCancelled, user }) => {
  const [itineraries, setItineraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState({});
  const [error, setError] = useState('');
  const [step, setStep] = useState(1); // 1: warning, 2: download options, 3: confirm
  const [confirmationText, setConfirmationText] = useState('');
  const [cancellationReason, setCancellationReason] = useState('');
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchItineraries();
    }
  }, [isOpen]);

  const fetchItineraries = async () => {
    setLoading(true);
    try {
      const data = await getUserItineraries();
      setItineraries(data || []);
    } catch (err) {
      console.error('Error fetching itineraries:', err);
      setError('Failed to load itineraries');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (itinerary) => {
    const statusKey = `pdf-${itinerary.request_id}`;
    setDownloadStatus(prev => ({ ...prev, [statusKey]: 'loading' }));

    try {
      // Fetch full itinerary content
      const fullItinerary = await getItinerary(itinerary.request_id);

      // Create a temporary element for PDF generation
      const tempDiv = document.createElement('div');
      tempDiv.style.padding = '20px';
      tempDiv.style.fontFamily = 'Arial, sans-serif';
      const safeStartLocation = DOMPurify.sanitize(fullItinerary.start_location || '');
      const safeEndLocation = DOMPurify.sanitize(fullItinerary.end_location || '');
      const safeStartDate = DOMPurify.sanitize(fullItinerary.start_date || 'Not specified');
      const safeEndDate = DOMPurify.sanitize(fullItinerary.end_date || 'Not specified');
      const safeTransportation = DOMPurify.sanitize(fullItinerary.transportation_mode || 'Not specified');
      const safeItinerary = DOMPurify.sanitize(fullItinerary.itinerary || 'No itinerary content available.');
      tempDiv.innerHTML = `
        <h1 style="color: #4D8760; margin-bottom: 10px;">Travel Itinerary</h1>
        <p style="color: #666; margin-bottom: 5px;"><strong>From:</strong> ${safeStartLocation}</p>
        <p style="color: #666; margin-bottom: 5px;"><strong>To:</strong> ${safeEndLocation}</p>
        <p style="color: #666; margin-bottom: 5px;"><strong>Dates:</strong> ${safeStartDate} - ${safeEndDate}</p>
        <p style="color: #666; margin-bottom: 20px;"><strong>Transportation:</strong> ${safeTransportation}</p>
        <hr style="border: 1px solid #ddd; margin: 20px 0;">
        <div style="white-space: pre-wrap; line-height: 1.6;">${safeItinerary}</div>
      `;
      document.body.appendChild(tempDiv);

      const opt = {
        margin: [15, 10, 15, 10],
        filename: `Itinerary-${fullItinerary.start_location}-to-${fullItinerary.end_location}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };

      await html2pdf().set(opt).from(tempDiv).save();
      document.body.removeChild(tempDiv);

      setDownloadStatus(prev => ({ ...prev, [statusKey]: 'done' }));
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setDownloadStatus(prev => ({ ...prev, [statusKey]: 'error' }));
    }
  };

  const handleDownloadPhotos = async (itinerary) => {
    const statusKey = `photos-${itinerary.request_id}`;
    setDownloadStatus(prev => ({ ...prev, [statusKey]: 'loading' }));

    try {
      let photos = [];
      try {
        const response = await getPhotos(itinerary.request_id);
        photos = response?.photos || response || [];
      } catch (apiErr) {
        // API might return 404 or error if no photos - that's okay
        console.log('No photos found or error fetching:', apiErr);
      }

      if (!photos || photos.length === 0) {
        setDownloadStatus(prev => ({ ...prev, [statusKey]: 'none' }));
        return;
      }

      // Download each photo
      for (let i = 0; i < photos.length; i++) {
        const photo = photos[i];
        const response = await fetch(`/api/photos/${photo.id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `photo-${itinerary.request_id}-${i + 1}.jpg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }

      setDownloadStatus(prev => ({ ...prev, [statusKey]: 'done' }));
    } catch (err) {
      console.error('Error downloading photos:', err);
      setDownloadStatus(prev => ({ ...prev, [statusKey]: 'error' }));
    }
  };

  const handleDownloadExpenses = async (itinerary) => {
    const statusKey = `expenses-${itinerary.request_id}`;
    setDownloadStatus(prev => ({ ...prev, [statusKey]: 'loading' }));

    try {
      let expenses = [];
      try {
        const response = await getExpenses(itinerary.request_id);
        expenses = response?.expenses || response || [];
      } catch (apiErr) {
        // API might return 404 or error if no expenses - that's okay
        console.log('No expenses found or error fetching:', apiErr);
      }

      if (!expenses || expenses.length === 0) {
        setDownloadStatus(prev => ({ ...prev, [statusKey]: 'none' }));
        return;
      }

      // Create CSV content
      let csvContent = 'Description,Amount,Currency,Category,Date,Paid By\n';
      expenses.forEach(exp => {
        csvContent += `"${exp.description}",${exp.amount},${exp.currency},"${exp.category}","${exp.date}","${exp.paid_by_name || 'Unknown'}"\n`;
      });

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `expenses-${itinerary.request_id}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      // Download receipts if any
      for (let i = 0; i < expenses.length; i++) {
        const exp = expenses[i];
        if (exp.has_receipt) {
          try {
            const response = await fetch(`/api/itinerary/${itinerary.request_id}/expenses/${exp.id}/receipt`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
              }
            });
            if (response.ok) {
              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `receipt-${exp.id}.jpg`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
            }
          } catch (e) {
            console.error('Error downloading receipt:', e);
          }
        }
      }

      setDownloadStatus(prev => ({ ...prev, [statusKey]: 'done' }));
    } catch (err) {
      console.error('Error downloading expenses:', err);
      setDownloadStatus(prev => ({ ...prev, [statusKey]: 'error' }));
    }
  };

  const handleDownloadAll = async (itinerary) => {
    await handleDownloadPDF(itinerary);
    await handleDownloadPhotos(itinerary);
    await handleDownloadExpenses(itinerary);
  };

  const handleConfirmCancel = async () => {
    // Validate confirmation text
    if (confirmationText !== 'CANCEL') {
      setError('Please type CANCEL to confirm cancellation.');
      return;
    }

    setCancelling(true);
    setError('');

    // Check if any downloads were completed
    const hasDownloaded = Object.values(downloadStatus).some(status => status === 'done');

    try {
      await cancelSubscription({
        confirmation: 'CANCEL',
        reason: cancellationReason || null,
        feedback: feedback || null,
        downloadedData: hasDownloaded
      });
      // Await the callback to ensure user data is refreshed before closing
      await onCancelled();
      onClose();
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to cancel subscription. Please try again.';
      setError(errorMessage);
      setCancelling(false);
    }
  };

  const getStatusIcon = (statusKey) => {
    const status = downloadStatus[statusKey];
    if (status === 'loading') return <FaSpinner className="animate-spin text-primary-500" />;
    if (status === 'done') return <FaCheckCircle className="text-secondary-500" />;
    if (status === 'none') return <span className="text-xs text-neutral-400">None</span>;
    if (status === 'error') return <span className="text-xs text-red-500">Error</span>;
    return null;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-red-50 border-b border-red-100 p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <FaExclamationTriangle className="text-red-500 text-xl" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-neutral-800">Cancel Subscription</h2>
                <p className="text-sm text-neutral-600">This action will downgrade you to the Demo plan</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-red-100 rounded-lg transition-colors"
            >
              <FaTimes className="text-neutral-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {step === 1 && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <h3 className="font-semibold text-amber-800 mb-2">Important Notice</h3>
                <ul className="text-sm text-amber-700 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="mt-1">•</span>
                    <span>Your subscription will remain active until the end of your current billing period.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1">•</span>
                    <span>After cancellation, you'll be downgraded to the <strong>Demo</strong> plan.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1">•</span>
                    <span><strong>Your existing itineraries will no longer be accessible</strong> on the Demo plan.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1">•</span>
                    <span>Premium features like PDF export, trip photos, and expense tracking will be disabled.</span>
                  </li>
                </ul>
              </div>

              <div className="bg-primary-50 border border-primary-200 rounded-xl p-4">
                <h3 className="font-semibold text-primary-800 mb-2">Before You Go</h3>
                <p className="text-sm text-primary-700">
                  We recommend downloading your itineraries, photos, and expense data before cancelling.
                  Click "Download My Data" to proceed.
                </p>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <p className="text-neutral-600">
                You have <strong>{itineraries.length}</strong> itinerary(s). Download your data before cancelling:
              </p>

              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <FaSpinner className="animate-spin text-2xl text-primary-500" />
                </div>
              ) : itineraries.length === 0 ? (
                <div className="text-center py-8 text-neutral-500">
                  <p>No itineraries found.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {itineraries.map((itin) => (
                    <div key={itin.request_id} className="bg-neutral-50 rounded-xl p-4 border border-neutral-300">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-neutral-800 flex items-center gap-2">
                            <FaMapMarkerAlt className="text-primary-500" />
                            {itin.start_location} → {itin.end_location}
                          </h4>
                          <p className="text-sm text-neutral-500 flex items-center gap-2 mt-1">
                            <FaCalendarAlt />
                            {itin.start_date || 'No date'} - {itin.end_date || 'No date'}
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handleDownloadPDF(itin)}
                          disabled={downloadStatus[`pdf-${itin.request_id}`] === 'loading'}
                          className="flex items-center gap-2 px-3 py-2 bg-white border border-neutral-300 rounded-lg text-sm hover:bg-neutral-50 disabled:opacity-50 transition-colors"
                        >
                          <FaFilePdf className="text-red-500" />
                          PDF
                          {getStatusIcon(`pdf-${itin.request_id}`)}
                        </button>

                        <button
                          onClick={() => handleDownloadPhotos(itin)}
                          disabled={downloadStatus[`photos-${itin.request_id}`] === 'loading'}
                          className="flex items-center gap-2 px-3 py-2 bg-white border border-neutral-300 rounded-lg text-sm hover:bg-neutral-50 disabled:opacity-50 transition-colors"
                        >
                          <FaImages className="text-blue-500" />
                          Photos
                          {getStatusIcon(`photos-${itin.request_id}`)}
                        </button>

                        <button
                          onClick={() => handleDownloadExpenses(itin)}
                          disabled={downloadStatus[`expenses-${itin.request_id}`] === 'loading'}
                          className="flex items-center gap-2 px-3 py-2 bg-white border border-neutral-300 rounded-lg text-sm hover:bg-neutral-50 disabled:opacity-50 transition-colors"
                        >
                          <FaReceipt className="text-amber-500" />
                          Expenses
                          {getStatusIcon(`expenses-${itin.request_id}`)}
                        </button>

                        <button
                          onClick={() => handleDownloadAll(itin)}
                          className="flex items-center gap-2 px-3 py-2 bg-primary-500 text-white rounded-lg text-sm hover:bg-primary-600 transition-colors"
                        >
                          <FaDownload />
                          All
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="py-4">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaExclamationTriangle className="text-red-500 text-2xl" />
                </div>
                <h3 className="text-lg font-bold text-neutral-800 mb-2">Confirm Cancellation</h3>
                <p className="text-neutral-600">
                  Your subscription will be cancelled at the end of your current billing period.
                  You will lose access to all premium features and your itineraries.
                </p>
              </div>

              {/* Reason for cancellation */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Why are you cancelling? (optional)
                </label>
                <select
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-neutral-300 rounded-lg focus:ring-4 focus:ring-primary-100 outline-none focus:border-transparent"
                >
                  <option value="">Select a reason...</option>
                  <option value="too_expensive">Too expensive</option>
                  <option value="not_using_enough">Not using it enough</option>
                  <option value="missing_features">Missing features I need</option>
                  <option value="found_alternative">Found a better alternative</option>
                  <option value="temporary_pause">Just pausing temporarily</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Feedback */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Any feedback for us? (optional)
                </label>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Help us improve..."
                  rows={2}
                  className="w-full px-4 py-2 border-2 border-neutral-300 rounded-lg focus:ring-4 focus:ring-primary-100 outline-none focus:border-transparent resize-none"
                />
              </div>

              {/* Confirmation input */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <label className="block text-sm font-medium text-red-700 mb-2">
                  Type <span className="font-bold">CANCEL</span> to confirm:
                </label>
                <input
                  type="text"
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value.toUpperCase())}
                  placeholder="Type CANCEL here"
                  className="w-full px-4 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-center font-mono text-lg"
                  autoComplete="off"
                />
              </div>

              {error && (
                <p className="text-red-600 text-sm mt-4 text-center">{error}</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-neutral-300 p-6 bg-neutral-50">
          <div className="flex justify-between gap-4">
            {step === 1 && (
              <>
                <button
                  onClick={onClose}
                  className="flex-1 px-6 py-3 border border-neutral-300 rounded-xl font-medium text-neutral-700 hover:bg-neutral-100 transition-colors"
                >
                  Keep Subscription
                </button>
                <button
                  onClick={() => setStep(2)}
                  className="flex-1 px-6 py-3 bg-primary-500 text-white rounded-xl font-medium hover:bg-primary-600 transition-colors flex items-center justify-center gap-2"
                >
                  <FaDownload />
                  Download My Data
                </button>
              </>
            )}

            {step === 2 && (
              <>
                <button
                  onClick={() => setStep(1)}
                  className="px-6 py-3 border border-neutral-300 rounded-xl font-medium text-neutral-700 hover:bg-neutral-100 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  className="flex-1 px-6 py-3 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors"
                >
                  Continue to Cancel
                </button>
              </>
            )}

            {step === 3 && (
              <>
                <button
                  onClick={() => {
                    setStep(2);
                    setConfirmationText('');
                    setError('');
                  }}
                  disabled={cancelling}
                  className="px-6 py-3 border border-neutral-300 rounded-xl font-medium text-neutral-700 hover:bg-neutral-100 transition-colors disabled:opacity-50"
                >
                  Back
                </button>
                <button
                  onClick={handleConfirmCancel}
                  disabled={cancelling || confirmationText !== 'CANCEL'}
                  className="flex-1 px-6 py-3 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {cancelling ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Cancelling...
                    </>
                  ) : (
                    'Confirm Cancellation'
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancellationModal;
